import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { getTeacherAssignments, getScannedDataByAssignment, getStudentGradingData, getQuestionPath, detectBlokMode } from '../services/yoklaService';
import { exportBuraxilisQuestionPDFFromImages } from '../services/pdfExportService';
import { YoklaAssignment, ScannedData, BlokMode } from '../types';
import { getApellyasiyasByTeacher } from '../services/apellyasiyaService';
import { AssignmentCardSkeleton, PageLoadingSkeleton } from './ui/Skeleton';
import { groupApellyasiyasByExam } from '../utils/apellyasiyaGrouping';
import { ExamApellyasiyaGroupCard } from './teacher/ExamApellyasiyaGroup';
import { displayFenn } from '../utils/fennUtils';
import { normalizeQrup } from '../constants/fennConfig';

// Vector Icon Components
const AssignmentIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>
    <polyline points="14 2 14 8 20 8"/>
  </svg>
);

const AppealIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"/>
    <path d="M9 12l2 2 4-4"/>
  </svg>
);

const ArchiveIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="4" rx="1"/>
    <path d="M4 8h16v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8z"/>
    <path d="M9 12h6"/>
  </svg>
);

// Buraxılış Icon - Graduation Cap
const BuraxilisIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
    <path d="M6 12v5c3 3 9 3 12 0v-5"/>
  </svg>
);

// Blok Icon - Education/Book
const BlokIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
    <path d="M12 6h5"/>
    <path d="M12 10h5"/>
    <path d="M12 14h5"/>
  </svg>
);

// Pool Icon - Multiple Teachers
const PoolIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);

const Container = styled.div`
  padding: 0;
  margin-bottom: 24px;
`;

const TabContainer = styled.div`
  background: white;
  border-radius: 16px;
  padding: 8px;
  margin-bottom: 24px;
  display: flex;
  gap: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`;

/** Wrapper to hide in-content tab bar on mobile when dashboard uses bottom nav */
const TabContainerWrapper = styled.div<{ $hideOnMobile?: boolean }>`
  ${props => props.$hideOnMobile && `
    @media (max-width: 768px) {
      display: none !important;
    }
  `}
`;

const TabButton = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 14px 20px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: ${props => props.$active
    ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
    : 'transparent'};
  color: ${props => props.$active ? 'white' : '#64748b'};
  box-shadow: ${props => props.$active
    ? '0 4px 12px rgba(59, 130, 246, 0.3)'
    : 'none'};

  &:hover {
    background: ${props => props.$active
      ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
      : '#f1f5f9'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    padding: 12px 16px;
    font-size: 14px;
  }
`;

const TabIcon = styled.span`
  display: flex;
  align-items: center;
  font-size: 18px;
`;

const Title = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 20px;
`;

const AssignmentCard = styled.div<{ $isActive: boolean; $isApellyasiya?: boolean }>`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-top: ${props => {
    if (props.$isApellyasiya) {
      return '3px solid #f59e0b';
    }
    return props.$isActive ? '3px solid #3b82f6' : '1px solid #e2e8f0';
  }};
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-color: ${props => {
      if (props.$isApellyasiya) {
        return '#f59e0b';
      }
      return props.$isActive ? '#3b82f6' : '#cbd5e1';
    }};
  }

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const AssignmentInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  flex-wrap: wrap;
  gap: 8px;

  @media (max-width: 768px) {
    margin-bottom: 10px;
  }
`;

const AssignmentTitle = styled.h4`
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const StudentCount = styled.span`
  background: #f1f5f9;
  color: #64748b;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
`;

const AssignmentDetails = styled.div`
  display: flex;
  gap: 16px;
  color: #64748b;
  font-size: 14px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 12px;
    font-size: 13px;
  }
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DetailLabel = styled.span`
  font-weight: 500;
  color: #64748b;
`;

const DetailValue = styled.span`
  font-weight: 600;
  color: #1e293b;
`;

const NoAssignmentsMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #64748b;
  font-size: 16px;
  background: #f8fafc;
  border-radius: 8px;
  border: 2px dashed #e2e8f0;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  color: #64748b;
`;

const LoadingSpinner = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid #e2e8f0;
  border-top: 3px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 12px;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ProgressSection = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 2px solid rgba(0, 0, 0, 0.05);
`;

const ProgressLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-size: 14px;
  color: #64748b;
`;

const ProgressBar = styled.div<{ percentage: number }>`
  width: 100%;
  height: 10px;
  background: #e2e8f0;
  border-radius: 5px;
  overflow: hidden;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${props => props.percentage}%;
    background: #10b981;
    transition: width 0.2s ease;
  }
`;

const QuestionProgressItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  background: #f8fafc;
  border-radius: 8px;
  margin-bottom: 8px;
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    background: #eef2ff;
    border-color: #c7d2fe;
    box-shadow: 0 2px 6px rgba(129, 140, 248, 0.25);
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    flex-wrap: wrap;
    align-items: center;
  }
`;

const OverallProgressWrapper = styled.div`
  margin-bottom: 12px;
`;

const OverallProgressText = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  color: #475569;
  margin-bottom: 6px;

  span:first-child {
    font-weight: 600;
  }

  span:last-child {
    font-weight: 600;
    color: #1e293b;
  }
`;

const QuestionProgressBar = styled.div<{ percentage: number }>`
  flex: 1 1 100%;
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  position: relative;
  display: block;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${props => props.percentage}%;
    background: #3b82f6;
    transition: width 0.2s ease;
  }

  @media (max-width: 768px) {
    width: 100%;
    margin-top: 6px;
    height: 10px;
    order: 4;
  }
`;

const QuestionProgressText = styled.div`
  min-width: 100px;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  text-align: left;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    gap: 6px;
    flex: 1;
    order: 1;
  }
`;

const QuestionProgressCount = styled.div`
  min-width: 50px;
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  text-align: right;

  @media (max-width: 768px) {
    order: 2;
  }
`;

const QuestionArrow = styled.div`
  min-width: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: #64748b;
  
  @media (max-width: 768px) {
    order: 3;
    margin-left: 2px;
  }
`;

const ExportPDFButton = styled.button`
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  background: #10b981;
  color: white;
  white-space: nowrap;
  flex-shrink: 0;
  margin-left: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

  &:hover {
    background: #059669;
    box-shadow: 0 2px 8px rgba(16, 185, 129, 0.25);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    order: 5;
    margin-left: 0;
    margin-top: 6px;
    width: 100%;
    justify-content: center;
  }
`;

const QuestionBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  background: #3b82f6;
  color: white;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  margin-right: 4px;
  margin-bottom: 4px;
`;

const FormatBadge = styled.span<{ $format: 'legacy' | 'new' }>`
  display: inline-block;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  margin-left: 8px;
  text-transform: uppercase;
  background: ${props => props.$format === 'new'
    ? 'linear-gradient(135deg, #10b981, #059669)'
    : 'linear-gradient(135deg, #64748b, #475569)'};
  color: white;
`;

// Pool Badge - Shows this is a pool assignment
const PoolBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  color: white;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  box-shadow: 0 2px 6px rgba(139, 92, 246, 0.3);
  margin-right: 8px;
`;

// Pool Teacher Info - Shows active teacher count
const PoolTeacherInfo = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #64748b;
  margin-left: 8px;
  background: #f1f5f9;
  padding: 4px 10px;
  border-radius: 12px;
`;

export interface DashboardTabCounts {
  assignments: number;
  appeals: number;
  archive: number;
}

interface AssignmentListProps {
  teacherId: string;
  onAssignmentSelect: (assignment: YoklaAssignment, students: ScannedData[]) => void;
  /** Controlled tab (dashboard bottom nav). When provided, parent controls active tab. */
  activeTab?: 'assignments' | 'appeals' | 'archive';
  onTabChange?: (tab: 'assignments' | 'appeals' | 'archive') => void;
  /** Report counts for bottom nav badges. Called when data is loaded/updated. */
  onCountsChange?: (counts: DashboardTabCounts) => void;
  /** When true, hide the in-content tab bar on mobile (bottom nav is used instead). */
  hideTabBarOnMobile?: boolean;
}

const AssignmentList: React.FC<AssignmentListProps> = ({
  teacherId,
  onAssignmentSelect,
  activeTab: controlledActiveTab,
  onTabChange,
  onCountsChange,
  hideTabBarOnMobile = false
}) => {
  const [internalActiveTab, setInternalActiveTab] = useState<'assignments' | 'appeals' | 'archive'>('assignments');
  const activeTab = controlledActiveTab ?? internalActiveTab;
  const setActiveTab = onTabChange ?? setInternalActiveTab;

  const [assignments, setAssignments] = useState<YoklaAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [studentCounts, setStudentCounts] = useState<{ [assignmentId: string]: number }>({});
  const [gradedCounts, setGradedCounts] = useState<{ [assignmentId: string]: number }>({});
  // Buraxılış üçün: hər sual üzrə müəllim tərəfindən yoxlanmış say və effektiv total (admin + digər hocalar çıxılmış)
  const [questionCompletions, setQuestionCompletions] = useState<{ [assignmentId: string]: { [questionNum: string]: number } }>({});
  const [questionTotals, setQuestionTotals] = useState<{ [assignmentId: string]: { [questionNum: string]: number } }>({});
  const [exportingPDF, setExportingPDF] = useState<{ [key: string]: boolean }>({});
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfModalData, setPdfModalData] = useState<{
    assignment: YoklaAssignment;
    questionNum: string;
    students: ScannedData[];
  } | null>(null);
  const [loadedImages, setLoadedImages] = useState<Map<string, HTMLImageElement>>(new Map());
  const [loadingImages, setLoadingImages] = useState<Set<string>>(new Set());
  const [imageUrls, setImageUrls] = useState<Map<string, string>>(new Map());
  const [apellyasiyas, setApellyasiyas] = useState<Map<string, any>>(new Map()); // assignmentId -> apellyasiya data
  const [apellyasiyaAssignments, setApellyasiyaAssignments] = useState<YoklaAssignment[]>([]);
  const [apellyasiyaGroups, setApellyasiyaGroups] = useState<ReturnType<typeof groupApellyasiyasByExam>>([]);
  const [assignmentFormats, setAssignmentFormats] = useState<{ [assignmentId: string]: BlokMode }>({});
  const [examArchiveStatus, setExamArchiveStatus] = useState<{ [collectionName: string]: boolean }>({});
  const [examData, setExamData] = useState<{ [collectionName: string]: { name: string; type: string } }>({});

  const checkExamArchivedStatus = async (collectionName: string): Promise<boolean> => {
    try {
      const { getExamByCollection } = await import('../services/examService');
      const exam = await getExamByCollection(collectionName);
      return exam?.archived || false;
    } catch (error) {
      console.error('Error checking exam archive status:', error);
      return false;
    }
  };

  useEffect(() => {
    const loadAssignments = async () => {
      try {
        console.log('🔍 AssignmentList: Ogretmen ID:', teacherId);
        setLoading(true);
        const teacherAssignments = await getTeacherAssignments(teacherId);
        console.log('📋 AssignmentList: Bulunan atamalar:', teacherAssignments);
        console.log('📊 AssignmentList: Atama sayisi:', teacherAssignments.length);
        
        // Sort by createdAt - newest first
        const sortedAssignments = [...teacherAssignments].sort((a, b) => {
          const timeA = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
          const timeB = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
          return timeB - timeA; // Newest first
        });
        
        // Her assignment'ı detaylı logla
        sortedAssignments.forEach((a, idx) => {
          console.log(`📝 Assignment ${idx + 1}:`, {
            id: a.id,
            fenn: a.fenn,
            sinif: a.sinif,
            variant: a.variant,
            qrup: a.qrup,
            examType: a.examType,
            questionNumbers: a.questionNumbers,
            questionNumber: a.questionNumber
          });
        });

        setAssignments(sortedAssignments);

        // Import getExamByCollection for fetching exam data
        const { getExamByCollection } = await import('../services/examService');

        // Check archive status AND fetch exam data for all unique collections
        const uniqueCollections = [...new Set(teacherAssignments.map(a => a.collectionName || 'scanned2610'))];

        const examDataPromises = uniqueCollections.map(async (collectionName) => {
          const [isArchived, exam] = await Promise.all([
            checkExamArchivedStatus(collectionName),
            getExamByCollection(collectionName)
          ]);
          return {
            collectionName,
            isArchived,
            examName: exam?.name || '',
            examType: exam?.type || ''
          };
        });

        const examDataResults = await Promise.all(examDataPromises);
        const newArchiveStatus: { [key: string]: boolean } = {};
        const newExamData: { [key: string]: { name: string; type: string } } = {};

        examDataResults.forEach(({ collectionName, isArchived, examName, examType }) => {
          newArchiveStatus[collectionName] = isArchived;
          if (examName) {
            newExamData[collectionName] = { name: examName, type: examType };
          }
        });

        setExamArchiveStatus(newArchiveStatus);
        setExamData(newExamData);
        console.log('📦 AssignmentList: Exam archive status loaded:', newArchiveStatus);
        console.log('📝 AssignmentList: Exam data loaded:', newExamData);
        
        // Group apellyasiyas by exam after exam data is loaded
        if (apellyasiyaAssignments.length > 0) {
          const groups = groupApellyasiyasByExam(apellyasiyaAssignments, newExamData);
          setApellyasiyaGroups(groups);
        }

        // Apellyasiyaları yüklə
        try {
          const apellyasiyaList = await getApellyasiyasByTeacher(teacherId);
          const apellyasiyaMap = new Map<string, any>();
          apellyasiyaList.forEach((ap: any) => {
            // Apellyasiya ID'si ile map oluştur (yoxla-s document ID)
            apellyasiyaMap.set(ap.id, ap);
          });
          console.log('✅ Apellyasiyalar yükləndi:', apellyasiyaMap.size, 'apellyasiya');
          console.log('📋 Apellyasiya ID-ləri:', Array.from(apellyasiyaMap.keys()));
          setApellyasiyas(apellyasiyaMap);
        } catch (error) {
          console.error('Apellyasiyalar yüklənərkən xəta:', error);
        }
        
        // TÜM atamaları paralel olarak işle (Promise.all)
        const results = await Promise.all(
          teacherAssignments.map(async (assignment) => {
            // CRITICAL: Skip loading student data if exam is archived
            const collectionName = assignment.collectionName || 'scanned2610';
            const isExamArchived = examArchiveStatus[collectionName];

            if (isExamArchived) {
              console.log(`📦 AssignmentList: Exam archived, skipping student loading - ${assignment.fenn} (${collectionName})`);
              return {
                assignmentId: assignment.id,
                studentCount: 0,
                gradedCount: 0,
                perQuestionCounts: {},
                perQuestionTotals: {},
                format: 'legacy' as BlokMode,
                isArchived: true // Flag to identify archived assignments
              };
            }

            try {
              const students = await getScannedDataByAssignment(assignment, teacherId);

              // Detect Blok format (legacy vs new) for Blok exams
              let detectedFormat: BlokMode = 'legacy';
              if (assignment.examType === 'Blok' && students.length > 0) {
                // Yeni format: assignment.questionNumbers varsa (per-question grading)
                // Köhnə format: questionNumbers yoxdur, fenn olaraq grading
                if (assignment.questionNumbers && assignment.questionNumbers.length > 0) {
                  detectedFormat = 'new';  // Q28, Q29, Q30 ayrı ayrı grading
                } else {
                  // Legacy Blok: student datasına əsasən detect et
                  detectedFormat = detectBlokMode(students[0]);
                }
              }

              const isBuraxilis = assignment.examType === 'buraxilis';
              const isBlokWithQuestions = assignment.examType === 'Blok' && assignment.questionNumbers && assignment.questionNumbers.length > 0;
              let gradedCount = 0;
              const perQuestionCounts: { [questionNum: string]: number } = {};
              const perQuestionTotals: { [questionNum: string]: number } = {};

              // Handle both buraxilis and Blok with questionNumbers array
              if ((isBuraxilis || isBlokWithQuestions) && assignment.questionNumbers && assignment.questionNumbers.length > 0) {
                // Buraxılış and Blok: hər sual üçün yalnız bu müəllimin məsuliyyətində olan şagirdləri say
                assignment.questionNumbers.forEach((qNum: string) => {
                  const qField = `${qNum}_c`;
                  let teacherCompleted = 0;
                  let effectiveTotal = 0;

                  students.forEach(student => {
                    const qValue: any = (student as any)[qField];

                    // Admin boş kağıt kontrolü - bu öğretmene ait değilse total'e dahil etme
                    const isAdminEmpty = qValue && 
                      typeof qValue === 'object' && 
                      qValue.hocaId === 'admin' && 
                      qValue.note && 
                      qValue.note.includes('Boş sual - Admin tərəfindən təsdiqləndi');
                    
                    // Admin boş kağıt işaretlemesi varsa bu soruyu bu öğretmenin assignment'ından çıkar
                    if (isAdminEmpty) {
                      return; // Bu soruyu sayma
                    }

                    if (!qValue) {
                      // Heç kim yoxlamayıb – bu müəllim üçün açıq sual
                      effectiveTotal++;
                    } else if (qValue && typeof qValue === 'object') {
                      // Admin və ya digər hoca tərəfindən yoxlamalar
                      if (qValue.hocaId === teacherId) {
                        teacherCompleted++;
                        effectiveTotal++; // Bu müəllim tərəfindən tamamlanıb
                      } else {
                        // Admin və ya başqa müəllim – bu müəllimin ümumi progressindən çıx
                        // Teacher panelde admin yoklamalarını göstərmirik
                      }
                    }
                  });

                  perQuestionCounts[qNum] = teacherCompleted;
                  perQuestionTotals[qNum] = effectiveTotal;
                });

                // Buraxılış and Blok: şagird səviyyəsində – bu müəllimə aid bütün sualları tamamlanan şagirdləri say
                students.forEach((student) => {
                  const allQuestionsGradedByThisTeacher = assignment.questionNumbers!.every((qNum: string) => {
                    const qField = `${qNum}_c`;
                    const qValue: any = (student as any)[qField];
                    return (
                      qValue &&
                      typeof qValue === 'object' &&
                      qValue.hocaId === teacherId &&
                      qValue.score !== undefined &&
                      qValue.score !== null &&
                      qValue.score !== ''
                    );
                  });

                  if (allQuestionsGradedByThisTeacher) {
                    gradedCount++;
                  }
                });

                console.log(`✅ ${isBuraxilis ? 'Buraxılış' : 'Blok'}: ${assignment.fenn} - ${assignment.questionNumbers.length} sual, ${gradedCount}/${students.length} şagird bu müəllim tərəfindən tamamlandı`);
              } else {
                // Blok: Original logic
                const gradingResults = await Promise.all(
                  students.map(async (student) => {
                    try {
                      const gradingData = await getStudentGradingData(student.student_id, assignment.fenn);
                      return gradingData && Object.keys(gradingData).length > 0 ? 1 : 0;
                    } catch (error) {
                      console.error('❌ AssignmentList: Grading data hatası:', error);
                      return 0;
                    }
                  })
                );
                
                gradedCount = gradingResults.reduce((sum: number, val: number) => sum + val, 0);
              }
              
              console.log(`👥 AssignmentList: ${assignment.fenn} - Sinif ${assignment.sinif} icin ${students.length} ogrenci, degerlendirilmis: ${gradedCount}`);
              
              return {
                assignmentId: assignment.id,
                studentCount: students.length,
                gradedCount,
                perQuestionCounts,
                perQuestionTotals: perQuestionTotals || {},
                format: detectedFormat
              };
            } catch (err) {
              console.error(`❌ AssignmentList: ${assignment.fenn} veri yuklenirken hata:`, err);
              return {
                assignmentId: assignment.id,
                studentCount: 0,
                gradedCount: 0,
                perQuestionCounts: {},
                format: 'legacy' as BlokMode
              };
            }
          })
        );

        // Sonuçları state'e aktar
        const counts: { [assignmentId: string]: number } = {};
        const gradedCountsMap: { [assignmentId: string]: number } = {};
        const questionComps: { [assignmentId: string]: { [questionNum: string]: number } } = {};
        const questionTotalsMap: { [assignmentId: string]: { [questionNum: string]: number } } = {};
        const formatsMap: { [assignmentId: string]: BlokMode } = {};

        results.forEach(result => {
          counts[result.assignmentId] = result.studentCount;
          gradedCountsMap[result.assignmentId] = result.gradedCount;
          questionComps[result.assignmentId] = result.perQuestionCounts;
          questionTotalsMap[result.assignmentId] = result.perQuestionTotals || {};
          formatsMap[result.assignmentId] = result.format || 'legacy';
        });

        setStudentCounts(counts);
        setGradedCounts(gradedCountsMap);
        setQuestionCompletions(questionComps);
        setQuestionTotals(questionTotalsMap);
        setAssignmentFormats(formatsMap);
      } catch (err: any) {
        console.error('❌ AssignmentList: Hata:', err);
        setError(err.message || 'Tapşırıqlar yüklənərkən xəta baş verdi');
      } finally {
        setLoading(false);
      }
    };

    if (teacherId) {
      console.log('🚀 AssignmentList: Tapşırıqlar yüklənir...');
      loadAssignments();
    } else {
      console.log('⚠️ AssignmentList: teacherId boş!');
    }
  }, [teacherId]);

  // Report counts for dashboard bottom nav badges: only incomplete yoxlamalar and incomplete apellyasiyalar
  useEffect(() => {
    if (!onCountsChange) return;

    const nonArchivedNonApellyasiya = assignments.filter(a => {
      const collectionName = a.collectionName || 'scanned2610';
      const isArchived = examArchiveStatus[collectionName];
      const isApellyasiya = (a as any).type === 'apellyasiya' || apellyasiyas.has(a.id);
      return !isArchived && !isApellyasiya;
    });

    const assignmentsCount = nonArchivedNonApellyasiya.filter(a => {
      const totalCount = studentCounts[a.id] ?? 0;
      const completedCount = gradedCounts[a.id] ?? 0;
      const hasQuestions = a.questionNumbers && a.questionNumbers.length > 0;

      if (hasQuestions) {
        let totalPossible = 0;
        let totalChecked = 0;
        a.questionNumbers!.forEach((qNum: string) => {
          const qTotal = questionTotals[a.id]?.[qNum] ?? totalCount;
          const qCompleted = questionCompletions[a.id]?.[qNum] ?? 0;
          totalPossible += qTotal;
          totalChecked += qCompleted;
        });
        if (totalPossible === 0) return false;
        return totalChecked < totalPossible;
      }

      if (totalCount === 0) return false;
      return completedCount < totalCount;
    }).length;

    const appealsCount = apellyasiyaAssignments.filter(a => {
      const collectionName = a.collectionName || 'scanned2610';
      if (examArchiveStatus[collectionName]) return false;
      const completed = a.status === 'completed' || (a as any).apellyasiyaStatusFromStudent === true;
      return !completed;
    }).length;

    const archivedAssignments = assignments.filter(a => {
      const collectionName = a.collectionName || 'scanned2610';
      const isArchived = examArchiveStatus[collectionName];
      const isApellyasiya = (a as any).type === 'apellyasiya' || apellyasiyas.has(a.id);
      return isArchived && !isApellyasiya;
    }).length;
    const archivedAppeals = apellyasiyaAssignments.filter(a => {
      const collectionName = a.collectionName || 'scanned2610';
      return examArchiveStatus[collectionName];
    }).length;

    onCountsChange({
      assignments: assignmentsCount,
      appeals: appealsCount,
      archive: archivedAssignments + archivedAppeals
    });
  }, [assignments, apellyasiyaAssignments, examArchiveStatus, apellyasiyas, studentCounts, gradedCounts, questionCompletions, questionTotals, onCountsChange]);

  // Modal açılanda şəkilləri yüklə
  useEffect(() => {
    if (pdfModalOpen && pdfModalData) {
      loadImagesForPDF(pdfModalData.assignment, pdfModalData.questionNum, pdfModalData.students);
    }
  }, [pdfModalOpen, pdfModalData]);

  // Apellyasiyaları assignment formatına çevir (student document'lerinden grade al)
  useEffect(() => {
    const loadApellyasiyaAssignments = async () => {
      if (apellyasiyas.size === 0) {
        setApellyasiyaAssignments([]);
        return;
      }
      
      try {
        const { getDoc, doc } = await import('firebase/firestore');
        const { db } = await import('../firebase');
        
        // Paralel olarak student document'lerini yüklə
        const assignmentPromises = Array.from(apellyasiyas.values()).map(async (ap: any) => {
          try {
            const studentDoc = await getDoc(doc(db, ap.collectionName, ap.studentDocumentId));
            const studentData = studentDoc.exists() ? studentDoc.data() : null;
            const grade = studentData?.grade || '';
            
            // Q##_c field'ından apellyasiya status'unu kontrol et
            const qField = `${ap.questionNumber}_c`;
            const gradingData: any = studentData?.[qField];
            // Use utility function - only apellyasiya_result === true means completed
            const { isApellyasiyaCompleted } = await import('../utils/apellyasiyaUtils');
            const isCompleted = isApellyasiyaCompleted(gradingData);
            
            return {
              id: ap.id,
              hocaId: ap.hocaId,
              hocaName: ap.hocaName,
              fenn: ap.fenn,
              sinif: String(grade || ''),
              qrup: studentData?.variant || ap.variant || '',
              variant: studentData?.variant || ap.variant,
              subGroup: undefined,
              status: isCompleted ? 'completed' : 'active',
              createdAt: ap.apellyasiya_tarixi,
              updatedAt: ap.updatedAt,
              completedCount: isCompleted ? 1 : 0,
              totalCount: 1,
              collectionName: ap.collectionName,
              examType: 'buraxilis' as const,
              questionNumber: ap.questionNumber,
              questionNumbers: [ap.questionNumber],
              type: 'apellyasiya' as any,
              // Q##_c field'ından apellyasiya status'unu sakla
              apellyasiyaStatusFromStudent: isCompleted
            } as YoklaAssignment;
          } catch (error) {
            console.error(`❌ Student document yüklənərkən xəta (${ap.studentDocumentId}):`, error);
            // Fallback: grade olmadan oluştur
            return {
              id: ap.id,
              hocaId: ap.hocaId,
              hocaName: ap.hocaName,
              fenn: ap.fenn,
              sinif: '',
              qrup: ap.variant || '',
              variant: ap.variant,
              subGroup: undefined,
              status: 'active',
              createdAt: ap.apellyasiya_tarixi,
              updatedAt: ap.updatedAt,
              completedCount: 0,
              totalCount: 1,
              collectionName: ap.collectionName,
              examType: 'buraxilis' as const,
              questionNumber: ap.questionNumber,
              questionNumbers: [ap.questionNumber],
              type: 'apellyasiya' as any,
              apellyasiyaStatusFromStudent: false
            } as YoklaAssignment;
          }
        });
        
        const assignments = await Promise.all(assignmentPromises);
        
        // Sort apellyasiyaAssignments by createdAt - newest first
        const sortedApellyasiyaAssignments = [...assignments].sort((a, b) => {
          const timeA = a.createdAt?.toMillis ? a.createdAt.toMillis() : (a.createdAt as any)?.seconds ? (a.createdAt as any).seconds * 1000 : 0;
          const timeB = b.createdAt?.toMillis ? b.createdAt.toMillis() : (b.createdAt as any)?.seconds ? (b.createdAt as any).seconds * 1000 : 0;
          return timeB - timeA; // Newest first
        });
        
        setApellyasiyaAssignments(sortedApellyasiyaAssignments);
        
        // Group apellyasiyas by exam (will be updated when examData is available)
      } catch (error) {
        console.error('❌ Apellyasiya assignment-ları yüklənərkən xəta:', error);
        setApellyasiyaAssignments([]);
      }
    };
    
    loadApellyasiyaAssignments();
  }, [apellyasiyas]);

  // Update apellyasiya groups when assignments or exam data changes
  useEffect(() => {
    if (apellyasiyaAssignments.length > 0 && Object.keys(examData).length > 0) {
      const groups = groupApellyasiyasByExam(apellyasiyaAssignments, examData);
      setApellyasiyaGroups(groups);
    } else {
      setApellyasiyaGroups([]);
    }
  }, [apellyasiyaAssignments, examData]);

  const handleAssignmentClick = async (assignment: YoklaAssignment, questionNum?: string) => {
    try {
      console.log('🎯 AssignmentList: Atama tiklandi:', assignment);
      console.log('🔍 AssignmentList: ExamType:', assignment.examType, 'QuestionNumber:', questionNum || assignment.questionNumber);
      
      // Apellyasiya atamaları üçün xüsusi işləmə
      const isApellyasiya = (assignment as any).type === 'apellyasiya';
      if (isApellyasiya) {
        console.log('📝 Apellyasiya ataması aşkarlanıb');
        const apellyasiyaData = apellyasiyas.get(assignment.id);
        if (!apellyasiyaData) {
          console.error('❌ Apellyasiya məlumatı tapılmadı. Assignment ID:', assignment.id);
          console.error('📋 Mövcud apellyasiyalar:', Array.from(apellyasiyas.keys()));
          alert('Apellyasiya məlumatı tapılmadı. Zəhmət olmasa səhifəni yeniləyin.');
          return;
        }
        
        console.log('✅ Apellyasiya məlumatı tapıldı:', apellyasiyaData);
        console.log('📚 Collection:', apellyasiyaData.collectionName, 'Student Doc ID:', apellyasiyaData.studentDocumentId);
        
        // Apellyasiya üçün student document-ini yüklə
        const { getDoc, doc } = await import('firebase/firestore');
        const { db } = await import('../firebase');
        
        try {
          const studentDoc = await getDoc(doc(db, apellyasiyaData.collectionName, apellyasiyaData.studentDocumentId));
          
          if (!studentDoc.exists()) {
            console.error('❌ Şagird sənədi tapılmadı:', apellyasiyaData.studentDocumentId);
            alert('Şagird sənədi tapılmadı. Zəhmət olmasa admin ilə əlaqə saxlayın.');
            return;
          }
          
          console.log('✅ Şagird sənədi tapıldı:', studentDoc.id);
          
          const studentData = {
            id: studentDoc.id,
            ...studentDoc.data(),
            student_id: studentDoc.data().student_id || studentDoc.id
          } as ScannedData;
          
          const finalQuestionNum = questionNum || apellyasiyaData.questionNumber || assignment.questionNumber;
          console.log('📝 Final question number:', finalQuestionNum);
          
          const assignmentWithQuestion: YoklaAssignment = {
            ...assignment,
            questionNumber: finalQuestionNum,
            collectionName: apellyasiyaData.collectionName,
            sinif: String(studentData.grade || ''),
            variant: studentData.variant || '',
            qrup: studentData.variant || '',
            fenn: apellyasiyaData.fenn,
            examType: 'buraxilis' as const,
            hocaId: apellyasiyaData.hocaId || assignment.hocaId,
            hocaName: apellyasiyaData.hocaName || assignment.hocaName
          };
          
          console.log('🚀 Apellyasiya assignment hazırlandı:', assignmentWithQuestion);
          console.log('👤 Student data:', studentData);
          
          onAssignmentSelect(assignmentWithQuestion, [studentData]);
          return;
        } catch (firebaseError: any) {
          console.error('❌ Firebase xətası:', firebaseError);
          alert(`Xəta: ${firebaseError.message || 'Naməlum xəta'}`);
          return;
        }
      }
      
      // For buraxılış, set the specific question number (sulları böl: fallback to first assigned question)
      const assignmentWithQuestion = {
        ...assignment,
        questionNumber: questionNum || assignment.questionNumbers?.[0] || assignment.questionNumber
      };
      
      const students = await getScannedDataByAssignment(assignmentWithQuestion, teacherId);
      console.log('👥 AssignmentList: Ogrenci sayisi:', students.length);
      console.log('📋 AssignmentList: Ogrenci verileri:', students);
      
      onAssignmentSelect(assignmentWithQuestion, students);
    } catch (err: any) {
      console.error('❌ AssignmentList: Ogrenci verileri alinirken hata:', err);
    }
  };

  const handleExportPDF = async (assignment: YoklaAssignment, questionNum: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering assignment click
    
    try {
      // Şagirdləri yüklə
      const assignmentWithQuestion = {
        ...assignment,
        questionNumber: questionNum
      };
      
      const students = await getScannedDataByAssignment(assignmentWithQuestion, teacherId);
      
      if (students.length === 0) {
        alert('Bu sual üçün şagird tapılmadı');
        return;
      }

      // Modal aç və şəkilləri yüklə
      setPdfModalData({
        assignment: assignmentWithQuestion,
        questionNum,
        students
      });
      setPdfModalOpen(true);
      setLoadedImages(new Map());
      setLoadingImages(new Set());
      setImageUrls(new Map());
      
      // Şəkilləri yüklə
      loadImagesForPDF(assignmentWithQuestion, questionNum, students);
    } catch (error: any) {
      console.error('❌ PDF modal açıla bilmədi:', error);
      alert(`Xəta: ${error.message || 'Naməlum xəta'}`);
    }
  };

  const loadImagesForPDF = (
    assignment: YoklaAssignment,
    questionNum: string,
    students: ScannedData[]
  ) => {
    const urlMap = new Map<string, string>();
    const loadingSet = new Set<string>();

    // Bütün şəkillərin URL-lərini topla
    for (const student of students) {
      // Use getQuestionPath utility to support both legacy and new formats
      const imageUrl = getQuestionPath(student, questionNum);

      if (!imageUrl) {
        continue;
      }

      const imageKey = student.id || student.student_id;
      urlMap.set(imageKey, imageUrl);
      loadingSet.add(imageKey);
    }

    setImageUrls(urlMap);
    setLoadingImages(new Set(loadingSet));

    // Şəkilləri tək-tək yüklə (BuraxilisYoxlamaPage-dəki kimi - crossOrigin olmadan)
    for (const [imageKey, imageUrl] of urlMap.entries()) {
      const img = new Image();
      // crossOrigin olmadan yüklə (BuraxilisYoxlamaPage-dəki kimi)
      
      img.onload = () => {
        setLoadedImages(prev => {
          const newMap = new Map(prev);
          newMap.set(imageKey, img);
          return newMap;
        });
        
        setLoadingImages(prev => {
          const newSet = new Set(prev);
          newSet.delete(imageKey);
          return newSet;
        });
      };
      
      img.onerror = () => {
        setLoadingImages(prev => {
          const newSet = new Set(prev);
          newSet.delete(imageKey);
          return newSet;
        });
        console.error(`❌ Şəkil yüklənə bilmədi: ${imageUrl}`);
      };
      
      img.src = imageUrl;
    }
  };

  const handleGeneratePDF = async () => {
    if (!pdfModalData) return;

    try {
      setExportingPDF(prev => ({ ...prev, [`${pdfModalData.assignment.id}-${pdfModalData.questionNum}`]: true }));

      // Canvas-a çevir və PDF yarat
      await exportBuraxilisQuestionPDFFromImages(
        pdfModalData.assignment,
        pdfModalData.questionNum,
        pdfModalData.students,
        loadedImages
      );

      alert(`✅ PDF uğurla yaradıldı! (${pdfModalData.students.length} şagird)`);
      setPdfModalOpen(false);
      setPdfModalData(null);
      setLoadedImages(new Map());
    } catch (error: any) {
      console.error('❌ PDF export xətası:', error);
      alert(`PDF export zamanı xəta baş verdi: ${error.message || 'Naməlum xəta'}`);
    } finally {
      setExportingPDF(prev => ({ ...prev, [`${pdfModalData?.assignment.id}-${pdfModalData?.questionNum}`]: false }));
    }
  };

  if (loading) {
    return (
      <Container>
        <Title>Yoxlama Tapşırıqlarınız</Title>

        {/* Tab Navigation - hidden on mobile when dashboard uses bottom nav */}
        <TabContainerWrapper $hideOnMobile={hideTabBarOnMobile}>
          <TabContainer>
            <TabButton $active={true}>
              <TabIcon><AssignmentIcon /></TabIcon>
              Tapşırıqlar
            </TabButton>
            <TabButton $active={false}>
              <TabIcon><AppealIcon /></TabIcon>
              Apellyasiyalar
            </TabButton>
            <TabButton $active={false}>
              <TabIcon><ArchiveIcon /></TabIcon>
              Arxiv
            </TabButton>
          </TabContainer>
        </TabContainerWrapper>

        {/* Skeleton Loading */}
        <AssignmentCardSkeleton count={3} />
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <NoAssignmentsMessage>
          Hata: {error}
        </NoAssignmentsMessage>
      </Container>
    );
  }

  // Don't return early - we need to show tabs even if there are no assignments (there might be appeals or archived items)

  // Filter assignments based on active tab and archived status
  let activeAssignments: YoklaAssignment[] = [];
  
  if (activeTab === 'assignments') {
    // Tapşırıqlar tab: only non-archived assignments (not appeals)
    // Check both type field and apellyasiyas map to ensure appeals are excluded
    activeAssignments = assignments.filter(a => {
      const collectionName = a.collectionName || 'scanned2610';
      const isArchived = examArchiveStatus[collectionName];
      const isApellyasiya = (a as any).type === 'apellyasiya' || apellyasiyas.has(a.id);
      return !isArchived && !isApellyasiya;
    });
  } else if (activeTab === 'appeals') {
    // Apellyasiyalar tab: use grouped view (filtered groups will be rendered separately)
    // Keep activeAssignments empty for appeals tab - we'll render groups instead
    activeAssignments = [];
  } else if (activeTab === 'archive') {
    // Arxiv tab: all archived items (both assignments and appeals)
    // Filter out appeals from assignments array to avoid duplicates
    const archivedAssignments = assignments.filter(a => {
      const collectionName = a.collectionName || 'scanned2610';
      const isArchived = examArchiveStatus[collectionName];
      const isApellyasiya = (a as any).type === 'apellyasiya' || apellyasiyas.has(a.id);
      return isArchived && !isApellyasiya; // Only non-appeal archived assignments
    });
    const archivedAppeals = apellyasiyaAssignments.filter(a => {
      const collectionName = a.collectionName || 'scanned2610';
      return examArchiveStatus[collectionName];
    });
    // Combine and sort by newest first
    activeAssignments = [...archivedAssignments, ...archivedAppeals].sort((a, b) => {
      const timeA = a.createdAt?.toMillis ? a.createdAt.toMillis() : (a.createdAt as any)?.seconds ? (a.createdAt as any).seconds * 1000 : 0;
      const timeB = b.createdAt?.toMillis ? b.createdAt.toMillis() : (b.createdAt as any)?.seconds ? (b.createdAt as any).seconds * 1000 : 0;
      return timeB - timeA; // Newest first
    });
  }

  return (
    <Container>
      <Title>Yoxlama Tapşırıqlarınız</Title>

      {/* Tab Navigation - hidden on mobile when dashboard uses bottom nav */}
      <TabContainerWrapper $hideOnMobile={hideTabBarOnMobile}>
        <TabContainer>
          <TabButton
            $active={activeTab === 'assignments'}
            onClick={() => setActiveTab('assignments')}
          >
            <TabIcon><AssignmentIcon /></TabIcon>
            Tapşırıqlar
          </TabButton>
          <TabButton
            $active={activeTab === 'appeals'}
            onClick={() => setActiveTab('appeals')}
          >
            <TabIcon><AppealIcon /></TabIcon>
            Apellyasiyalar ({apellyasiyaAssignments.filter(a => {
              const collectionName = a.collectionName || 'scanned2610';
              return !examArchiveStatus[collectionName];
            }).length})
          </TabButton>
          <TabButton
            $active={activeTab === 'archive'}
            onClick={() => setActiveTab('archive')}
          >
            <TabIcon><ArchiveIcon /></TabIcon>
            Arxiv
          </TabButton>
        </TabContainer>
      </TabContainerWrapper>

      {/* Appeals tab: render grouped view */}
      {activeTab === 'appeals' ? (
        (() => {
          const nonArchivedGroups = apellyasiyaGroups.filter(group => 
            !examArchiveStatus[group.collectionName]
          );
          
          if (nonArchivedGroups.length === 0) {
            return (
              <NoAssignmentsMessage>
                Sizə hələ heç bir apellyasiya gəlib.
              </NoAssignmentsMessage>
            );
          }
          
          return (
            <>
              {nonArchivedGroups.map((group) => (
                <ExamApellyasiyaGroupCard
                  key={group.collectionName}
                  group={group}
                  onGroupClick={(collectionName) => {
                    // For now, just scroll to first appeal in group
                    // Batch view can be added later if needed
                    if (group.appeals.length > 0) {
                      const firstAppeal = group.appeals[0];
                      handleAssignmentClick(firstAppeal);
                    }
                  }}
                  onAppealClick={(appeal) => {
                    handleAssignmentClick(appeal);
                  }}
                />
              ))}
            </>
          );
        })()
      ) : activeAssignments.length === 0 ? (
        <NoAssignmentsMessage>
          {activeTab === 'assignments'
            ? 'Sizə hələ heç bir yoxlama tapşırığı verilməyib.'
            : 'Arxivdə heç bir element yoxdur.'}
        </NoAssignmentsMessage>
      ) : (
        activeAssignments.map((assignment) => {
          // Check both type field and apellyasiyas map for better detection
          const isApellyasiya = (assignment as any).type === 'apellyasiya' || apellyasiyas.has(assignment.id);
          const apellyasiyaData = isApellyasiya ? apellyasiyas.get(assignment.id) : null;
          const collectionName = assignment.collectionName || 'scanned2610';
          const isExamArchived = examArchiveStatus[collectionName];

          // For archived exams OR archived appeals, show info-only card
          if (isExamArchived) {
          return (
            <AssignmentCard
              key={`archived-${assignment.id}`}
              $isActive={false}
              $isApellyasiya={isApellyasiya}
              style={{
                background: '#fef3c7',
                borderColor: '#f59e0b',
                cursor: 'not-allowed',
                opacity: 0.85
              }}
            >
              <AssignmentInfo>
                <AssignmentTitle>
                  {/* Badge to distinguish appeals from regular assignments in archive */}
                  {isApellyasiya ? (
                    <span style={{
                      display: 'inline-block',
                      background: '#f59e0b',
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: '700',
                      marginRight: '8px',
                      textTransform: 'uppercase'
                    }}>
                      📝 Apellyasiya
                    </span>
                  ) : (
                    <span style={{
                      display: 'inline-block',
                      background: '#3b82f6',
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: '700',
                      marginRight: '8px',
                      textTransform: 'uppercase'
                    }}>
                      📋 Yoxlama
                    </span>
                  )}
                  {/* Exam name as header */}
                  {examData[collectionName]?.name && (
                    <>
                      <div style={{
                        fontSize: '13px',
                        fontWeight: '600',
                        color: '#6366f1',
                        marginBottom: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <span style={{
                          display: 'flex',
                          alignItems: 'center',
                          opacity: 0.9
                        }}>
                          {examData[collectionName].type === 'buraxilis' ? <BuraxilisIcon /> : <BlokIcon />}
                        </span>
                        {examData[collectionName].name}
                      </div>
                      <div style={{ width: '100%', height: '1px', background: '#e2e8f0', marginBottom: '8px' }} />
                    </>
                  )}
                  {displayFenn(assignment.fenn)}
                  {isApellyasiya ? ` - Apellyasiya` : ` - Sinif ${assignment.sinif}`}
                  {assignment.variant && ` - Variant ${assignment.variant}`}
                  {assignment.qrup && ` - Qrup ${normalizeQrup(assignment.qrup) || assignment.qrup}`}
                  <span style={{
                    marginLeft: '8px',
                    fontSize: '12px',
                    background: '#f59e0b',
                    color: 'white',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontWeight: '600'
                  }}>
                    Arxivləşib
                  </span>
                </AssignmentTitle>
                <StudentCount>
                  ℹ️ {isApellyasiya ? 'Apellyasiya arxivləşib' : 'Yoxlama yekunlaşıb'}
                </StudentCount>
              </AssignmentInfo>

              <AssignmentDetails>
                <DetailItem>
                  <DetailLabel>Fənn</DetailLabel>
                  <DetailValue>{displayFenn(assignment.fenn)}</DetailValue>
                </DetailItem>
                {!isApellyasiya && (
                  <DetailItem>
                    <DetailLabel>Sinif</DetailLabel>
                    <DetailValue>{assignment.sinif}</DetailValue>
                  </DetailItem>
                )}
                {assignment.variant && (
                  <DetailItem>
                    <DetailLabel>Variant</DetailLabel>
                    <DetailValue>{assignment.variant}</DetailValue>
                  </DetailItem>
                )}
                {assignment.qrup && (
                  <DetailItem>
                    <DetailLabel>Qrup</DetailLabel>
                    <DetailValue>{normalizeQrup(assignment.qrup) || assignment.qrup}</DetailValue>
                  </DetailItem>
                )}
                <DetailItem>
                  <DetailLabel>Vəziyyət</DetailLabel>
                  <DetailValue style={{ color: '#f59e0b', fontWeight: '600' }}>
                    📦 {isApellyasiya ? 'Apellyasiya arxivləşib' : 'Yoxlama yekunlaşıb'}
                  </DetailValue>
                </DetailItem>
              </AssignmentDetails>

              <div style={{
                padding: '12px',
                background: '#fffbeb',
                borderRadius: '8px',
                marginTop: '12px',
                border: '1px solid #fde68a',
                textAlign: 'center',
                fontSize: '14px',
                color: '#92400e',
                fontWeight: '500'
              }}>
                ℹ️ Bu imtahan arxivləşib. {isApellyasiya
                  ? 'Arxivləşmiş apellyasiyalar üçün admin panelinə müraciət edin.'
                  : 'Təfərrüatları görmək üçün admin panelinə müraciət edin.'}
              </div>
            </AssignmentCard>
          );
        }

        const totalCount = studentCounts[assignment.id] || 0;
        const completedCount = gradedCounts[assignment.id] || 0;
        const progress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
        // For appeals, always treat as buraxilis (appeals are always for buraxilis exams)
        const effectiveExamType = isApellyasiya ? 'buraxilis' : assignment.examType;
        const isBuraxilis = effectiveExamType === 'buraxilis';
        const isBlokWithQuestions = effectiveExamType === 'Blok' && assignment.questionNumbers && assignment.questionNumbers.length > 0;

        // Buraxılış and Blok with questionNumbers: eyni tapşırıq üçün BÜTÜN sualları bir kartda göstər
        if ((isBuraxilis || isBlokWithQuestions) && assignment.questionNumbers && assignment.questionNumbers.length > 0) {
          const questions = assignment.questionNumbers;

          // Ümumi irəliləmə: bütün suallar üzrə toplam yoxlanan / toplam potensial
          let totalChecked = 0;
          let totalPossible = 0;

          questions.forEach((questionNum: string) => {
            const qCompleted = questionCompletions[assignment.id]?.[questionNum] || 0;
            const qTotal =
              questionTotals[assignment.id]?.[questionNum] !== undefined
                ? questionTotals[assignment.id][questionNum]
                : totalCount;
            totalChecked += qCompleted;
            totalPossible += qTotal;
          });

          const overallProgress = totalPossible > 0 ? (totalChecked / totalPossible) * 100 : 0;
          const isEmpty = totalPossible === 0;

          return (
            <AssignmentCard
              key={`${isApellyasiya ? 'apellyasiya-' : ''}${assignment.id}`}
              $isActive={false}
              $isApellyasiya={isApellyasiya}
              style={isEmpty ? {
                background: 'linear-gradient(135deg, #fffbeb, #fef3c7)',
                borderTop: '3px solid #f59e0b',
                opacity: 0.85
              } : {}}
            >
              <AssignmentInfo>
                {isEmpty && (
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: '#f59e0b',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: 700,
                    padding: '4px 12px',
                    borderRadius: '8px',
                    marginBottom: '10px'
                  }}>⚠ Boş atama</div>
                )}
                <AssignmentTitle>
                  {isApellyasiya && (
                    <span style={{
                      display: 'inline-block',
                      background: '#f59e0b',
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: '700',
                      marginRight: '8px',
                      textTransform: 'uppercase'
                    }}>
                      📝 Apellyasiya
                    </span>
                  )}
                  {/* Pool Assignment Badge */}
                  {assignment.assignmentType === 'pool' && (
                    <PoolBadge>
                      <PoolIcon />
                      Hovuz Ataması
                    </PoolBadge>
                  )}
                  {/* Exam name as header */}
                  {examData[collectionName]?.name && (
                    <>
                      <div style={{
                        fontSize: '13px',
                        fontWeight: '600',
                        color: '#6366f1',
                        marginBottom: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <span style={{
                          display: 'flex',
                          alignItems: 'center',
                          opacity: 0.9
                        }}>
                          {examData[collectionName].type === 'buraxilis' ? <BuraxilisIcon /> : <BlokIcon />}
                        </span>
                        {examData[collectionName].name}
                      </div>
                      <div style={{ width: '100%', height: '1px', background: '#e2e8f0', marginBottom: '8px' }} />
                    </>
                  )}
                  {displayFenn(assignment.fenn)} {isApellyasiya ? '' : `- Sinif ${assignment.sinif}`}
                  {!isBuraxilis && assignmentFormats[assignment.id] && (
                    <FormatBadge $format={assignmentFormats[assignment.id]}>
                      {assignmentFormats[assignment.id] === 'new' ? 'Yeni format' : 'Köhnə format'}
                    </FormatBadge>
                  )}
                  {assignment.buildingName && assignment.buildingName !== 'Hamısı' && (
                    <span style={{
                      marginLeft: '8px',
                      fontSize: '14px',
                      color: '#64748b'
                    }}>
                      • {assignment.buildingName}
                    </span>
                  )}
                </AssignmentTitle>
                <StudentCount>
                  {isApellyasiya ? '1 apellyasiya' : `${totalCount} şagird`}
                </StudentCount>
                {/* Pool assignment teacher count */}
                {assignment.assignmentType === 'pool' && assignment.poolConfig && (
                  <PoolTeacherInfo>
                    <PoolIcon />
                    Aktiv: {assignment.poolConfig.teachers.filter(t => t.isActive).length}/{assignment.poolConfig.teachers.length} müəllim
                  </PoolTeacherInfo>
                )}
              </AssignmentInfo>
              {isApellyasiya && apellyasiyaData && (
                <div style={{
                  background: '#fffbeb',
                  padding: '12px',
                  borderRadius: '8px',
                  marginBottom: '12px',
                  borderLeft: '4px solid #f59e0b'
                }}>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#92400e', marginBottom: '6px' }}>
                    Apellyasiya Məlumatları:
                  </div>
                  <div style={{ fontSize: '12px', color: '#78350f', lineHeight: '1.8' }}>
                    <div><strong>Sual:</strong> {apellyasiyaData.questionNumber}</div>
                    <div><strong>Şagird ID:</strong> {apellyasiyaData.studentId}</div>
                    <div><strong>Səbəb:</strong> {apellyasiyaData.apellyasiya_səbəbi}</div>
                    {apellyasiyaData.əvvəlki_yoxlama && (
                      <div style={{ marginTop: '6px', paddingTop: '6px', borderTop: '1px solid #fde68a' }}>
                        <div><strong>Əvvəlki bal:</strong> {apellyasiyaData.əvvəlki_yoxlama.score}</div>
                        <div><strong>Əvvəlki müəllim:</strong> {apellyasiyaData.əvvəlki_yoxlama.hoca}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              <AssignmentDetails>
                <DetailItem>
                  <DetailLabel>Fənn</DetailLabel>
                  <DetailValue>{displayFenn(assignment.fenn)}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Sinif</DetailLabel>
                  <DetailValue>{assignment.sinif}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Variant</DetailLabel>
                  <DetailValue>{assignment.variant || assignment.qrup}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Tip</DetailLabel>
                  <DetailValue style={{ color: isApellyasiya ? '#f59e0b' : isBlokWithQuestions ? '#f59e0b' : '#10b981' }}>
                    {isApellyasiya ? 'Buraxılış (Apellyasiya)' : isBlokWithQuestions ? '📚 Blok' : 'Buraxılış'}
                  </DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Vəziyyət</DetailLabel>
                  <DetailValue style={{ 
                    color: isApellyasiya 
                      ? (assignment.status === 'completed' ? '#10b981' : '#f59e0b')
                      : (assignment.status === 'completed' ? '#10b981' : 
                         assignment.status === 'active' ? '#3b82f6' : '#64748b')
                  }}>
                    {isApellyasiya
                      ? (assignment.status === 'completed' ? 'Tamamlandı' : 'Gözləyir')
                      : (assignment.status === 'completed' ? 'Tamamlandı' : 
                         assignment.status === 'active' ? 'Aktiv' : 'Passiv')}
                  </DetailValue>
                </DetailItem>
              </AssignmentDetails>
              
              {/* Ümumi irəliləyiş + sual-sual irəliləyiş */}
              {!isApellyasiya ? (
              <ProgressSection>
                <OverallProgressWrapper>
                  <OverallProgressText>
                    <span>Ümumi irəliləyiş</span>
                    <span>
                      {totalChecked}/{totalPossible} yoxlama
                    </span>
                  </OverallProgressText>
                  <ProgressBar percentage={overallProgress} />
                  <div style={{ 
                    marginTop: '6px', 
                    fontSize: '12px', 
                    color: '#64748b',
                    textAlign: 'right'
                  }}>
                    %{Math.round(overallProgress)} tamamlandı
                  </div>
                </OverallProgressWrapper>

                <ProgressLabel>
                  <span>Sual üzrə irəliləyiş</span>
                </ProgressLabel>
                {questions.map((questionNum: string) => {
                  const qCompleted = questionCompletions[assignment.id]?.[questionNum] || 0;
                  const qTotal =
                    questionTotals[assignment.id]?.[questionNum] !== undefined
                      ? questionTotals[assignment.id][questionNum]
                      : totalCount;
                  const qProgress = qTotal > 0 ? (qCompleted / qTotal) * 100 : 0;
                  const questionNumberDisplay = questionNum.replace(/^Q/i, '');

                  const exportKey = `${assignment.id}-${questionNum}`;
                  const isExporting = exportingPDF[exportKey] || false;

                  return (
                    <QuestionProgressItem
                      key={`${assignment.id}-${questionNum}`}
                      onClick={isEmpty ? undefined : () => handleAssignmentClick(assignment, questionNum)}
                      style={{ cursor: isEmpty ? 'not-allowed' : 'pointer', opacity: isEmpty ? 0.6 : 1 }}
                    >
                      <QuestionProgressText>
                        <QuestionBadge>{`Sual ${questionNumberDisplay}`}</QuestionBadge>
                        <span style={{ marginLeft: 4 }}>
                          {qCompleted}/{qTotal} şagird
                        </span>
                      </QuestionProgressText>
                    <QuestionProgressBar percentage={qProgress} />
                    <QuestionProgressCount>
                      %{Math.round(qProgress)}
                    </QuestionProgressCount>
                    {/* PDF Export button - temporarily hidden */}
                    {/* <ExportPDFButton
                      onClick={(e) => handleExportPDF(assignment, questionNum, e)}
                      disabled={isExporting}
                      title="PDF Export"
                    >
                      {isExporting ? '⏳' : '📄'} {isExporting ? 'Export edilir...' : 'PDF Export'}
                    </ExportPDFButton> */}
                    <QuestionArrow>
                      ›
                    </QuestionArrow>
                    </QuestionProgressItem>
                  );
                })}
              </ProgressSection>
              ) : (
                // Apellyasiya üçün özel buton
                <div style={{
                  padding: '16px',
                  background: '#fffbeb',
                  borderRadius: '8px',
                  border: '2px dashed #fbbf24',
                  textAlign: 'center'
                }}>
                  <div style={{ marginBottom: '12px', fontSize: '14px', color: '#78350f' }}>
                    Apellyasiyanı yoxlamaq üçün klikləyin
                  </div>
                  <button
                    onClick={() => {
                      if (apellyasiyaData && apellyasiyaData.questionNumber) {
                        console.log('📝 Apellyasiya yoxla butonuna basıldı:', apellyasiyaData);
                        handleAssignmentClick(assignment, apellyasiyaData.questionNumber);
                      } else {
                        console.error('❌ Apellyasiya məlumatı və ya sual nömrəsi tapılmadı:', { apellyasiyaData, assignment });
                        alert('Apellyasiya məlumatı tapılmadı. Zəhmət olmasa yenidən cəhd edin.');
                      }
                    }}
                    style={{
                      padding: '12px 24px',
                      background: '#f59e0b',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '15px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background = '#d97706';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(245, 158, 11, 0.25)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = '#f59e0b';
                      e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.08)';
                    }}
                  >
                    📝 Apellyasiyanı Yoxla
                  </button>
                </div>
              )}
            </AssignmentCard>
          );
        }

        // Blok without questionNumbers (legacy format): Single card as before
        // Check if old-format Blok is 100% complete - disable the card
        const isOldFormatBlok = assignment.examType === 'Blok' &&
                                (!assignment.questionNumbers || assignment.questionNumbers.length === 0);
        const isFullyComplete = isOldFormatBlok && totalCount > 0 && completedCount === totalCount;
        const isBlokEmpty = totalCount === 0;

        return (
          <AssignmentCard
            key={`assignment-${assignment.id}`}
            $isActive={false}
            $isApellyasiya={false}
            onClick={isFullyComplete || isBlokEmpty ? undefined : () => handleAssignmentClick(assignment)}
            style={isBlokEmpty ? {
              background: 'linear-gradient(135deg, #fffbeb, #fef3c7)',
              borderTop: '3px solid #f59e0b',
              cursor: 'not-allowed',
              opacity: 0.85
            } : isFullyComplete ? {
              cursor: 'not-allowed',
              opacity: '0.7',
              background: '#f8fafc'
            } : {}}
          >
            <AssignmentInfo>
              {isBlokEmpty && (
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: '#f59e0b',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 700,
                  padding: '4px 12px',
                  borderRadius: '8px',
                  marginBottom: '10px'
                }}>⚠ Boş atama</div>
              )}
              <AssignmentTitle>
                {/* Exam name as header */}
                {examData[collectionName]?.name && (
                  <>
                    <div style={{
                      fontSize: '13px',
                      fontWeight: '600',
                      color: '#6366f1',
                      marginBottom: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <span style={{
                        display: 'flex',
                        alignItems: 'center',
                        opacity: 0.9
                      }}>
                        {examData[collectionName].type === 'buraxilis' ? <BuraxilisIcon /> : <BlokIcon />}
                      </span>
                      {examData[collectionName].name}
                    </div>
                    <div style={{ width: '100%', height: '1px', background: '#e2e8f0', marginBottom: '8px' }} />
                  </>
                )}
                {displayFenn(assignment.fenn)} - Sinif {assignment.sinif}
                {assignmentFormats[assignment.id] && (
                  <FormatBadge $format={assignmentFormats[assignment.id]}>
                    {assignmentFormats[assignment.id] === 'new' ? 'Yeni format' : 'Köhnə format'}
                  </FormatBadge>
                )}
                {assignment.buildingName && assignment.buildingName !== 'Hamısı' && (
                  <span style={{
                    marginLeft: '8px',
                    fontSize: '14px',
                    color: '#64748b'
                  }}>
                    • {assignment.buildingName}
                  </span>
                )}
              </AssignmentTitle>
              <StudentCount>
                {studentCounts[assignment.id] !== undefined 
                  ? `${completedCount}/${totalCount} şagird` 
                  : 'Yüklənir...'
                }
              </StudentCount>
            </AssignmentInfo>
            <AssignmentDetails>
              <DetailItem>
                <DetailLabel>Fənn</DetailLabel>
                <DetailValue>{displayFenn(assignment.fenn)}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Sinif</DetailLabel>
                <DetailValue>{assignment.sinif}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Qrup</DetailLabel>
                <DetailValue>{normalizeQrup(assignment.qrup) || assignment.qrup}{assignment.subGroup && ` (${assignment.subGroup})`}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Tip</DetailLabel>
                <DetailValue style={{ color: '#f59e0b' }}>
                  📚 Blok
                </DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Vəziyyət</DetailLabel>
                <DetailValue style={{ 
                  color: assignment.status === 'completed' ? '#10b981' : 
                         assignment.status === 'active' ? '#3b82f6' : '#64748b'
                }}>
                  {assignment.status === 'completed' ? 'Tamamlandı' : 
                   assignment.status === 'active' ? 'Aktiv' : 'Passiv'}
                </DetailValue>
              </DetailItem>
            </AssignmentDetails>
            
            {/* Progress Section - Blok only */}
            <ProgressSection>
              {isFullyComplete && (
                <div style={{
                  padding: '10px',
                  background: '#dcfce7',
                  borderRadius: '8px',
                  marginBottom: '12px',
                  border: '1px solid #86efac',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#166534' }}>
                    ✅ Tamamlandı - Bütün şagirdlər yoxlanılıb
                  </div>
                </div>
              )}
              <ProgressLabel>
                <span>İləriləmə</span>
                <span style={{ fontWeight: 600, color: '#1e293b' }}>
                  {completedCount}/{totalCount}
                </span>
              </ProgressLabel>
              <ProgressBar percentage={progress} />
              <div style={{
                marginTop: '8px',
                fontSize: '12px',
                color: '#64748b',
                textAlign: 'right'
              }}>
                %{Math.round(progress)} tamamlandı
              </div>
            </ProgressSection>
          </AssignmentCard>
        );
      })
      )}

      {/* PDF Export Modal */}
      {pdfModalOpen && pdfModalData && (
        <PDFExportModal
          assignment={pdfModalData.assignment}
          questionNum={pdfModalData.questionNum}
          students={pdfModalData.students}
          loadedImages={loadedImages}
          loadingImages={loadingImages}
          onClose={() => {
            setPdfModalOpen(false);
            setPdfModalData(null);
            setLoadedImages(new Map());
            setLoadingImages(new Set());
            setImageUrls(new Map());
          }}
          imageUrls={imageUrls}
          onExport={handleGeneratePDF}
          isExporting={exportingPDF[`${pdfModalData.assignment.id}-${pdfModalData.questionNum}`] || false}
        />
      )}
    </Container>
  );
};

// PDF Export Modal Component
const PDFExportModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
`;

const PDFExportModalContent = styled.div`
  background: white;
  border-radius: 20px;
  padding: 32px;
  max-width: 1200px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);

  @media (max-width: 768px) {
    padding: 24px;
  }
`;

const PDFExportModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid #f1f5f9;
`;

const PDFExportModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const PDFExportModalCloseButton = styled.button`
  background: none;
  border: none;
  font-size: 28px;
  color: #64748b;
  cursor: pointer;
  padding: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: #f1f5f9;
    color: #1e293b;
  }
`;

const PDFExportModalBody = styled.div`
  margin-bottom: 24px;
`;

const PDFExportModalImages = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PDFExportModalImageItem = styled.div`
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  background: #f8fafc;
`;

const PDFExportModalImageHeader = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 12px;
`;

const PDFExportModalImageContainer = styled.div`
  position: relative;
  width: 100%;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border-radius: 8px;
  overflow: hidden;
`;

const PDFExportModalImage = styled.img`
  max-width: 100%;
  max-height: 400px;
  height: auto;
  display: block;
`;

const PDFExportModalLoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #e2e8f0;
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const PDFExportModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 16px;
  border-top: 2px solid #f1f5f9;
`;

const PDFExportModalButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;

  ${props => props.variant === 'secondary' ? `
    background: #f1f5f9;
    color: #475569;
    &:hover {
      background: #e2e8f0;
    }
  ` : `
    background: #3b82f6;
    color: white;
    &:hover {
      background: #2563eb;
      box-shadow: 0 4px 12px rgba(59, 130, 246, 0.25);
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

interface PDFExportModalProps {
  assignment: YoklaAssignment;
  questionNum: string;
  students: ScannedData[];
  loadedImages: Map<string, HTMLImageElement>;
  loadingImages: Set<string>;
  imageUrls: Map<string, string>;
  onClose: () => void;
  onExport: () => void;
  isExporting: boolean;
}

const PDFExportModal: React.FC<PDFExportModalProps> = ({
  assignment,
  questionNum,
  students,
  loadedImages,
  loadingImages,
  imageUrls,
  onClose,
  onExport,
  isExporting
}) => {
  const questionNumberDisplay = questionNum.replace(/^Q/i, '');
  const allImagesLoaded = loadingImages.size === 0 && loadedImages.size > 0;
  const sortedStudents = [...students].sort((a, b) => {
    const idA = a.id || a.student_id || '';
    const idB = b.id || b.student_id || '';
    return idA.localeCompare(idB);
  });

  const maskName = (firstName: string, lastName: string) => {
    const maskedFirst = firstName.charAt(0) + '*'.repeat(Math.max(1, firstName.length - 1));
    const maskedLast = '*'.repeat(Math.max(1, lastName.length));
    return `${maskedFirst} ${maskedLast}`;
  };

  return (
    <PDFExportModalOverlay onClick={onClose}>
      <PDFExportModalContent onClick={(e) => e.stopPropagation()}>
        <PDFExportModalHeader>
          <PDFExportModalTitle>
            PDF Export - {displayFenn(assignment.fenn)} - Sual {questionNumberDisplay}
          </PDFExportModalTitle>
          <PDFExportModalCloseButton onClick={onClose}>×</PDFExportModalCloseButton>
        </PDFExportModalHeader>

        <PDFExportModalBody>
          <div style={{ marginBottom: '16px', fontSize: '14px', color: '#64748b' }}>
            {loadingImages.size > 0 
              ? `Şəkillər yüklənir... (${loadedImages.size}/${students.length})`
              : allImagesLoaded
              ? `✅ Bütün şəkillər yükləndi (${loadedImages.size}/${students.length})`
              : 'Şəkillər yüklənir...'}
          </div>

          <PDFExportModalImages>
            {sortedStudents.map((student, index) => {
              const imageKey = student.id || student.student_id;
              const img = loadedImages.get(imageKey);
              const isLoading = loadingImages.has(imageKey);
              const imageUrl = imageUrls.get(imageKey);

              return (
                <PDFExportModalImageItem key={imageKey}>
                  <PDFExportModalImageHeader>
                    {index + 1}. {maskName(student.firstName, student.lastName)} - {displayFenn(assignment.fenn)} Qiymətləndirməsi - {questionNumberDisplay}. sual
                  </PDFExportModalImageHeader>
                  <PDFExportModalImageContainer>
                    {isLoading ? (
                      <PDFExportModalLoadingSpinner />
                    ) : imageUrl ? (
                      <PDFExportModalImage 
                        src={imageUrl} 
                        alt={`${student.student_id}`}
                        style={{ display: 'block' }}
                      />
                    ) : (
                      <div style={{ color: '#64748b', fontSize: '14px' }}>Şəkil tapılmadı</div>
                    )}
                  </PDFExportModalImageContainer>
                </PDFExportModalImageItem>
              );
            })}
          </PDFExportModalImages>
        </PDFExportModalBody>

        <PDFExportModalFooter>
          <PDFExportModalButton variant="secondary" onClick={onClose} disabled={isExporting}>
            Ləğv et
          </PDFExportModalButton>
          <PDFExportModalButton 
            onClick={onExport} 
            disabled={!allImagesLoaded || isExporting}
          >
            {isExporting ? '⏳ PDF yaradılır...' : '📄 PDF Export'}
          </PDFExportModalButton>
        </PDFExportModalFooter>
      </PDFExportModalContent>
    </PDFExportModalOverlay>
  );
};

export default AssignmentList;
